"use client";

import { Session } from "@supabase/supabase-js";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSupabaseClient } from "../components/SupabaseProvider";
import { useIsEmbedded } from "./useIsEmbedded";

const LOG_PREFIX = "[external-session]";

type LogLevel = "info" | "warn" | "error";

const log = (level: LogLevel, event: string, payload: Record<string, unknown> = {}) => {
  try {
    const line = {
      event,
      timestamp: new Date().toISOString(),
      ...payload,
    };
    if (level === "error") {
      console.error(LOG_PREFIX, event, line);
    } else if (level === "warn") {
      console.warn(LOG_PREFIX, event, line);
    } else {
      console.info(LOG_PREFIX, event, line);
    }
  } catch {
    // never let logging throw
  }
};

const redactToken = (token: string) => {
  if (!token) return { preview: "", length: 0 };
  if (token.length <= 10) return { preview: `${token.slice(0, 2)}…${token.slice(-2)}`, length: token.length };
  return {
    preview: `${token.slice(0, 6)}…${token.slice(-4)}`,
    length: token.length,
  };
};

const decodeJwtExp = (jwt: string): { exp: number | null; secondsUntilExp: number | null } => {
  try {
    const parts = jwt.split(".");
    if (parts.length !== 3) return { exp: null, secondsUntilExp: null };
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    if (typeof payload.exp !== "number") return { exp: null, secondsUntilExp: null };
    return {
      exp: payload.exp,
      secondsUntilExp: Math.round(payload.exp - Date.now() / 1000),
    };
  } catch {
    return { exp: null, secondsUntilExp: null };
  }
};

const listSupabaseAuthKeys = (): string[] => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return [];
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && /^sb-.*-auth-token$/.test(k)) keys.push(k);
    }
    return keys;
  } catch {
    return [];
  }
};

const safeReferrer = () => {
  try {
    return document.referrer || null;
  } catch {
    return null;
  }
};

const safeSupabaseHost = () => {
  try {
    const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
    return url ? new URL(url).host : null;
  } catch {
    return null;
  }
};

const isReallyEmbedded = () => {
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
};

const makeAttemptId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `att_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const readTokenFromHash = (): string | null => {
  const hash = window.location.hash;
  if (!hash.includes("external_access_token")) return null;
  return hash.split("=")[1] || null;
};

// Session hook for the managed editor embed: a customer iframes the editor
// with `#external_access_token=…` (see klap-api/API_V2_EMBED.md), we sign in
// from that token and report the outcome to the host via `LOGIN_STATUS`.
//
// Invariant: nothing in here may add an entry to the browser's session
// history. An iframe shares the joint session history with its host page, so
// a `pushState` or fragment navigation from inside the embed either swallows
// the host's Back button (the press only undoes our entry and looks dead), or,
// when it happens without a user activation, trips Chrome's history
// manipulation intervention, which marks the host's own SPA entries as
// skippable so Back jumps out of the host site entirely. A "back-trap"
// sentinel that lived here until Sep 2026 did exactly that. Hosts that want
// Back to close the editor push their own entry on their side.
export const useExternalSession = () => {
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(true);
  const [externalSession, setExternalSession] = useState<Session | null>(null);
  const [externalSessionError, setExternalSessionError] = useState<
    string | null
  >(null);
  const supabaseClient = useSupabaseClient();
  const hasAttemptedExternalSession = useRef(false); // Prevent multiple attempts (strict mode dev)
  const attemptInFlightRef = useRef(false);
  const isEmbedded = useIsEmbedded();

  const notifyLoginStatus = useCallback(
    (
      status: "success" | "error",
      extra: {
        attemptId: string;
        reason?: string;
        elapsedMs?: number;
        errorCode?: string;
        errorName?: string;
        errorStatus?: number;
        errorMessage?: string;
        retryable?: boolean;
      } = { attemptId: "" }
    ) => {
      const targetOrigin = "*"; // TODO(theo): replace with client domain in production

      // Notify parent
      window.parent.postMessage(
        {
          type: "LOGIN_STATUS",
          data: {
            timestamp: new Date().toISOString(),
            status: status,
            attemptId: extra.attemptId,
            reason: extra.reason,
            elapsedMs: extra.elapsedMs,
            errorCode: extra.errorCode,
            errorName: extra.errorName,
            errorStatus: extra.errorStatus,
            errorMessage: extra.errorMessage,
            retryable: extra.retryable,
          },
        },
        targetOrigin
      );

      log(status === "success" ? "info" : "error", "notify_parent", {
        attemptId: extra.attemptId,
        status,
        reason: extra.reason,
        elapsedMs: extra.elapsedMs,
        errorCode: extra.errorCode,
        errorName: extra.errorName,
        errorStatus: extra.errorStatus,
        retryable: extra.retryable,
        targetOrigin,
      });
    },
    []
  );

  useEffect(() => {
    const effectAttemptId = makeAttemptId();

    const hashToken =
      typeof window !== "undefined" ? readTokenFromHash() : null;

    log("info", "effect_mount", {
      attemptId: effectAttemptId,
      pathname,
      isEmbedded,
      reallyEmbedded: typeof window !== "undefined" ? isReallyEmbedded() : null,
      referrer: typeof window !== "undefined" ? safeReferrer() : null,
      supabaseHost: safeSupabaseHost(),
      hashHasToken: Boolean(hashToken),
      hasAttemptedBefore: hasAttemptedExternalSession.current,
    });

    const signInFromExternalToken = async (
      externalHashedAccessToken: string,
      attemptId: string = makeAttemptId()
    ) => {
      const startedAt = performance.now();
      const elapsed = () => Math.round(performance.now() - startedAt);

      if (attemptInFlightRef.current) {
        log("warn", "attempt_skipped_in_flight", { attemptId });
        return;
      }
      attemptInFlightRef.current = true;
      setIsLoading(true);
      setExternalSessionError(null);

      const tokenInfo = redactToken(externalHashedAccessToken);

      // Strip the token from the URL without creating a new history entry.
      // Assigning `location.hash` is a fragment navigation, which adds an
      // entry to the host page's joint session history: the host's browser
      // Back button would then only undo the hash change inside the iframe
      // and look dead. `replaceState` mutates the current entry instead.
      try {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.hash = "";
        window.history.replaceState(window.history.state, "", cleanUrl.toString());
      } catch {
        window.location.hash = "";
      }

      // Pre-verify state snapshot
      try {
        const { data: preData } = await supabaseClient.auth.getSession();
        const preSession = preData?.session ?? null;
        log("info", "pre_verify_state", {
          attemptId,
          token: tokenInfo,
          hasExistingSession: Boolean(preSession),
          existingUserId: preSession?.user?.id ?? null,
          existingExpiresAt: preSession?.expires_at ?? null,
          existingTokenExp: preSession
            ? decodeJwtExp(preSession.access_token)
            : null,
          localStorageAuthKeys: listSupabaseAuthKeys(),
        });
      } catch (preErr) {
        log("warn", "pre_verify_state_failed", {
          attemptId,
          error: (preErr as Error)?.message,
        });
      }

      try {
        const verifyStartedAt = performance.now();
        log("info", "verify_otp_start", {
          attemptId,
          token: tokenInfo,
          elapsedMs: elapsed(),
        });


        const { data, error } = await supabaseClient.auth.verifyOtp({
          type: "magiclink",
          token_hash: externalHashedAccessToken,
        });

        const verifyElapsedMs = Math.round(performance.now() - verifyStartedAt);

        if (error) {
          log("error", "verify_otp_error", {
            attemptId,
            token: tokenInfo,
            verifyElapsedMs,
            elapsedMs: elapsed(),
            errorName: (error as Error & { name?: string })?.name,
            errorMessage: error.message,
            errorStatus: (error as { status?: number })?.status,
            errorCode: (error as { code?: string })?.code,
          });
          throw error;
        }
        if (!data?.session?.access_token || !data?.session?.refresh_token) {
          log("error", "verify_otp_missing_tokens", {
            attemptId,
            verifyElapsedMs,
            elapsedMs: elapsed(),
            hasSession: Boolean(data?.session),
            hasAccessToken: Boolean(data?.session?.access_token),
            hasRefreshToken: Boolean(data?.session?.refresh_token),
          });
          throw new Error("No access token supplied.");
        }

        log("info", "verify_otp_success", {
          attemptId,
          verifyElapsedMs,
          elapsedMs: elapsed(),
          userId: data.session.user?.id ?? null,
          expiresAt: data.session.expires_at ?? null,
          expiresIn: data.session.expires_in ?? null,
          accessTokenExp: decodeJwtExp(data.session.access_token),
          hasProviderToken: Boolean(data.session.provider_token),
        });

        // Ensure any existing authenticated state is cleared so the new session fully replaces it
        try {
          // AuthHelper.removeAllCookies();
          // await supabaseClient.auth.signOut();
        } catch (_) { }

        const setSessionStartedAt = performance.now();
        const accessTokenBefore = data.session.access_token;
        log("info", "set_session_start", {
          attemptId,
          elapsedMs: elapsed(),
          msSinceVerify: Math.round(setSessionStartedAt - verifyStartedAt - verifyElapsedMs),
        });

        // sign in with the access token
        const authResponse = await supabaseClient.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });

        const setSessionElapsedMs = Math.round(
          performance.now() - setSessionStartedAt
        );

        if (authResponse.error) {
          log("error", "set_session_error", {
            attemptId,
            setSessionElapsedMs,
            elapsedMs: elapsed(),
            errorName: (authResponse.error as Error & { name?: string })?.name,
            errorMessage: authResponse.error.message,
            errorStatus: (authResponse.error as { status?: number })?.status,
            errorCode: (authResponse.error as { code?: string })?.code,
          });
          throw authResponse.error;
        }

        const accessTokenAfter = authResponse.data.session?.access_token ?? null;
        log("info", "set_session_success", {
          attemptId,
          setSessionElapsedMs,
          elapsedMs: elapsed(),
          userId: authResponse.data.session?.user?.id ?? null,
          // A different access_token here means supabase-js silently refreshed
          // during setSession — strong signal that the redundant setSession is
          // the failure surface for transient refresh errors.
          accessTokenChanged: accessTokenAfter !== accessTokenBefore,
          accessTokenExp: accessTokenAfter ? decodeJwtExp(accessTokenAfter) : null,
        });

        setExternalSession(authResponse.data.session);
        notifyLoginStatus("success", { attemptId, elapsedMs: elapsed() });
      } catch (error) {
        console.error(error);
        const err = error as Error & {
          status?: number;
          code?: string;
          name?: string;
        };
        const reason = err?.code || err?.name || err?.message || "unknown";
        // Network failures / 5xx never consumed the token, so the parent can
        // retry with the same one. otp_expired & co. need a freshly minted token.
        const retryable =
          err?.name === "AuthRetryableFetchError" ||
          (typeof err?.status === "number" && err.status >= 500);
        log("error", "sign_in_failed", {
          attemptId,
          elapsedMs: elapsed(),
          reason,
          errorMessage: err?.message,
          errorStatus: err?.status,
          errorCode: err?.code,
          retryable,
        });
        setExternalSessionError((error as Error).message);
        notifyLoginStatus("error", {
          attemptId,
          reason,
          elapsedMs: elapsed(),
          errorCode: err?.code,
          errorName: err?.name,
          errorStatus: err?.status,
          errorMessage: err?.message,
          retryable,
        });
      } finally {
        attemptInFlightRef.current = false;
        setIsLoading(false);
      }
    };

    // The parent may retry a failed login by swapping only the
    // #external_access_token fragment on the iframe src. That is a
    // same-document navigation: no reload, no pathname change, so neither this
    // effect nor the initial-attempt path below would ever see the new token.
    const onHashChange = () => {
      const retryToken = readTokenFromHash();
      if (!retryToken) return;
      const retryAttemptId = makeAttemptId();
      log("info", "hash_retry_detected", {
        attemptId: retryAttemptId,
        pathname,
        token: redactToken(retryToken),
      });
      hasAttemptedExternalSession.current = true;
      signInFromExternalToken(retryToken, retryAttemptId);
    };
    window.addEventListener("hashchange", onHashChange);

    if (hashToken) {
      if (hasAttemptedExternalSession.current) {
        log("info", "guard_hit_already_attempted", { attemptId: effectAttemptId, pathname });
      } else {
        hasAttemptedExternalSession.current = true;
        signInFromExternalToken(hashToken, effectAttemptId);
      }
    } else {
      log("info", "no_token_in_hash", { attemptId: effectAttemptId, pathname });
      setIsLoading(false);
    }

    return () => {
      window.removeEventListener("hashchange", onHashChange);
    };
  }, [pathname]);

  return { isLoading, externalSession, externalSessionError };
};
